import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { formatCurrencyNumber } from '../../../../utils/numbers';
import { MEMBERSHIP_TERMS_URL } from '../../../../config/externalURL';
import { ROUTE_MY_ACCOUNT } from '../../../../config/routes';

import './SubscriptionOrderSummary.scss';

const WHAT_NEXT = [
  'Starter Kit ships in 3-5 business days.',
  'Fill your bags, drop them, earn TrashieCash.',
  'Cash out for gift cards at any time.',
];

/**
 * Figma 6462:23292 — marketing-style social proof (design copy), not a live metric.
 * Replace with a real count from analytics/API when product wants an exact number.
 */
const UNLIMITED_USERS_COUNT = '15,000+';

export const SubscriptionOrderSummaryLegal = () => (
  <div className="SubscriptionOrderSummary__legal">
    <section>
      <h4 className="SubscriptionOrderSummary__legalTitle">Membership &amp; Renewal</h4>
      <ul>
        <li>
          Your membership is annual and automatically renews each year unless canceled prior.
        </li>
        <li>We&apos;ll remind you a month before your renewal period begins.</li>
        <li>You can manage your membership anytime in your account settings.</li>
        <li>
          Your membership is designed for household use: 1 account holder, up to 2 household
          addresses (for example, home + office, or home + vacation house).
        </li>
      </ul>
    </section>
    <section>
      <h4 className="SubscriptionOrderSummary__legalTitle">Cancellation Policy</h4>
      <ul>
        <li>
          Cancel within 3 business days of Membership purchase with no usage = full refund.
        </li>
        <li>
          Once you&apos;ve used any benefits (like requesting bags or earning TrashieCash™),
          refunds are no longer available and your membership will be available for the
          remainder of the term.
        </li>
        <li>
          To cancel your renewal, go to your
          {' '}
          <Link
            className="SubscriptionOrderSummary__legalInlineLink"
            to={ROUTE_MY_ACCOUNT}
          >
            account settings
          </Link>
          {' '}
          or contact us at
          {' '}
          <a
            className="SubscriptionOrderSummary__legalInlineLink"
            href="mailto:support@trashie.com"
          >
            support@trashie.com
          </a>
          .
        </li>
      </ul>
    </section>
    <p className="SubscriptionOrderSummary__termsLink">
      To learn more,
      {' '}
      <a href={MEMBERSHIP_TERMS_URL} target="_blank" rel="noopener noreferrer">
        view our Terms of service
      </a>
      .
    </p>
  </div>
);

const SubscriptionOrderSummary = ({
  className,
  membershipDisplayPrice,
  subscriptionInterval = 'yr',
  isCouponApplied = false,
  fullValue = null,
  discountedValue = null,
  estimatedTaxDollars = null,
  hideLegal = false,
  hideCardTitle = false,
}) => {
  const intervalLabel = subscriptionInterval === 'yr' ? '/ yr' : ` / ${subscriptionInterval}`;

  const planPrice =
    typeof membershipDisplayPrice === 'number' && membershipDisplayPrice >= 0
      ? `${formatCurrencyNumber(membershipDisplayPrice)}${intervalLabel}`
      : '—';

  const subtotalAmount = isCouponApplied && discountedValue != null
    ? discountedValue
    : membershipDisplayPrice;
  const hasTax = typeof estimatedTaxDollars === 'number'
    && estimatedTaxDollars > 0;
  const todayAmount = hasTax
    ? subtotalAmount + estimatedTaxDollars
    : subtotalAmount;

  return (
    <aside className={classNames('SubscriptionOrderSummary', className)}>
      <div className="SubscriptionOrderSummary__card">
        {!hideCardTitle && (
          <div className="SubscriptionOrderSummary__cardHeader">
            <h2 className="SubscriptionOrderSummary__cardTitle">Unlimited Membership</h2>
          </div>
        )}
        <div className="SubscriptionOrderSummary__cardBody">
          <div className="SubscriptionOrderSummary__breakdown">
            <div className="SubscriptionOrderSummary__breakdownRow">
              <div className="SubscriptionOrderSummary__breakdownText">
                <p className="SubscriptionOrderSummary__breakdownLabel">Plan</p>
                <p className="SubscriptionOrderSummary__breakdownDesc">Unlimited bags and boxes</p>
              </div>
              <p className="SubscriptionOrderSummary__breakdownValue">{planPrice}</p>
            </div>
            <div className="SubscriptionOrderSummary__breakdownRule" aria-hidden="true" />

            <div className="SubscriptionOrderSummary__breakdownRow">
              <div className="SubscriptionOrderSummary__breakdownText">
                <p className="SubscriptionOrderSummary__breakdownLabel">Starter Kit</p>
                <p className="SubscriptionOrderSummary__breakdownDesc">Three bags and one tech box</p>
              </div>
              <p className="SubscriptionOrderSummary__breakdownValue">Included</p>
            </div>
            <div className="SubscriptionOrderSummary__breakdownRule" aria-hidden="true" />

            <div className="SubscriptionOrderSummary__breakdownRow SubscriptionOrderSummary__breakdownRow--simple">
              <p className="SubscriptionOrderSummary__breakdownLabel">Shipping</p>
              <p className="SubscriptionOrderSummary__breakdownValue">Free</p>
            </div>
            <div className="SubscriptionOrderSummary__breakdownRule" aria-hidden="true" />

            <div className="SubscriptionOrderSummary__breakdownRow SubscriptionOrderSummary__breakdownRow--simple">
              <p className="SubscriptionOrderSummary__breakdownLabel">Est. tax</p>
              <p className="SubscriptionOrderSummary__breakdownValue">
                {hasTax
                  ? formatCurrencyNumber(estimatedTaxDollars)
                  : '—'}
              </p>
            </div>
            <div className="SubscriptionOrderSummary__breakdownRule" aria-hidden="true" />

            <div className="SubscriptionOrderSummary__breakdownToday">
              <p className="SubscriptionOrderSummary__breakdownTodayLabel">Today</p>
              <p className="SubscriptionOrderSummary__breakdownTodayAmount">
                {isCouponApplied && fullValue != null && (
                  <span className="SubscriptionOrderSummary__strike">
                    {formatCurrencyNumber(fullValue)}
                  </span>
                )}
                <span className="SubscriptionOrderSummary__todayFigure">
                  {formatCurrencyNumber(todayAmount ?? 0)}
                </span>
              </p>
            </div>
          </div>

          <div className="SubscriptionOrderSummary__next">
            <h3 className="SubscriptionOrderSummary__nextTitle">What Happens Next</h3>
            <ol className="SubscriptionOrderSummary__nextList">
              {WHAT_NEXT.map((line, i) => (
                <li key={line} className="SubscriptionOrderSummary__nextItem">
                  <span className="SubscriptionOrderSummary__nextBadge">{i + 1}</span>
                  <span className="SubscriptionOrderSummary__nextText">{line}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="SubscriptionOrderSummary__socialBanner" role="status">
            <p className="SubscriptionOrderSummary__socialBannerText">
              <span className="SubscriptionOrderSummary__socialBannerEmphasis">
                {`${UNLIMITED_USERS_COUNT} `}
              </span>
              unlimited users and counting...
            </p>
          </div>

          <a
            href={MEMBERSHIP_TERMS_URL}
            className="SubscriptionOrderSummary__cardTerms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms and Conditions
          </a>
        </div>
      </div>

      {!hideLegal && <SubscriptionOrderSummaryLegal />}
    </aside>
  );
};

export default SubscriptionOrderSummary;
